import { useState, useReducer, useCallback, useEffect } from 'react';
import { initBot } from './init.js';
import 'wretch';
import 'wretch/addons/queryString';
import '../../utils/mParticle/mParticle.js';
import { storage } from '../../utils/storage/storageWrapper.js';
import 'fromentries';
import { getSalesforceLanguage } from './getSalesforceLanguage.js';
import { getConversationId } from './getConversationId.js';

const setPrechat = (botInstance, prechat, profileAccessToken) => {
    var _a;
    if (prechat === null || prechat === void 0 ? void 0 : prechat.visible) {
        botInstance.prechatAPI.setVisiblePrechatFields(prechat.visible);
    }
    const hidden = {
        ...prechat === null || prechat === void 0 ? void 0 : prechat.hidden,
        profileAccessToken: profileAccessToken !== null && profileAccessToken !== void 0 ? profileAccessToken : '',
        externalLanguage: getSalesforceLanguage(((_a = prechat === null || prechat === void 0 ? void 0 : prechat.hidden) === null || _a === void 0 ? void 0 : _a.externalLanguage) || 'en_GB'),
    };
    if (hidden) {
        botInstance.prechatAPI.setHiddenPrechatFields(hidden);
    }
};
const useSalesforceBot = ({ onChatOpen, onChatClose, getTokens, initialPrechat, ...botProps }) => {
    var _a;
    const storageName = `${botProps.init.orgId}_WEB_STORAGE`;
    const conversationId = getConversationId((_a = storage.local.getItem(storageName)) !== null && _a !== void 0 ? _a : storage.session.getItem(storageName));
    const chatOpenReducer = (isOpen, payload) => {
        if (payload.type === 'OPEN_CHAT' && !isOpen) {
            onChatOpen && onChatOpen(payload.conversationId);
            return true;
        }
        if (payload.type === 'CLOSE_CHAT' && isOpen) {
            onChatClose && onChatClose(payload.conversationId);
            return false;
        }
        return isOpen;
    };
    const [isLoading, setIsLoading] = useState(false);
    const [isReady, setIsReady] = useState(false);
    const [isOpen, dispatchIsOpen] = useReducer(chatOpenReducer, false);
    const [botInstance, setBotInstance] = useState(null);
    const [tokens, setTokens] = useState(null);
    const setIdentityToken = useCallback(async () => {
        const newTokens = getTokens && (await getTokens());
        if (newTokens) {
            setTokens(newTokens);
            setPrechat(window.embeddedservice_bootstrap, initialPrechat, newTokens.profileAccessToken);
            window.embeddedservice_bootstrap.userVerificationAPI.setIdentityToken({
                identityTokenType: 'JWT',
                identityToken: newTokens.chatSessionToken,
            });
        }
        else {
            window.embeddedservice_bootstrap.userVerificationAPI.clearSession();
        }
    }, [getTokens, initialPrechat]);
    useEffect(() => {
        window.addEventListener('onEmbeddedMessagingReady', setIdentityToken, {
            once: true,
        });
        window.addEventListener('onEmbeddedMessagingIdentityTokenExpired', setIdentityToken);
        return () => {
            window.removeEventListener('onEmbeddedMessagingIdentityTokenExpired', setIdentityToken);
        };
    }, [botInstance, setIdentityToken]);
    useEffect(() => {
        const handleBotReady = () => {
            dispatchIsOpen({ type: 'CLOSE_CHAT', conversationId });
            setIsReady(true);
            setIsLoading(false);
        };
        const handleBotOpen = () => {
            dispatchIsOpen({ type: 'OPEN_CHAT', conversationId });
            setIsReady(false);
            setIsLoading(false);
        };
        window.addEventListener('onEmbeddedMessagingReady', handleBotReady);
        window.addEventListener('onEmbeddedMessagingInitSuccess', handleBotOpen);
        return () => {
            window.removeEventListener('onEmbeddedMessagingReady', handleBotReady);
            window.removeEventListener('onEmbeddedMessagingInitSuccess', handleBotOpen);
        };
    }, [conversationId]);
    const loadBot = useCallback(async () => {
        if (botInstance || isReady || isLoading) {
            throw new Error(isLoading
                ? 'Trying to load bot when it is still loading'
                : 'Trying to load bot when it is already loaded');
        }
        setIsLoading(true);
        setIsReady(false);
        const bot = await initBot(botProps);
        setBotInstance(bot);
        setIsLoading(false);
    }, [
        botInstance,
        isReady,
        isLoading,
        botProps,
        setBotInstance,
        setIsLoading,
        setIsReady,
    ]);
    const launchChat = useCallback(async (prechat) => {
        if (!isReady || isLoading || !botInstance) {
            throw new Error('Trying to launch chat when bot is not ready');
        }
        setIsLoading(true);
        setIsReady(false);
        setPrechat(botInstance, prechat, tokens === null || tokens === void 0 ? void 0 : tokens.profileAccessToken);
        await botInstance.utilAPI.launchChat();
        setIsLoading(false);
    }, [isReady, isLoading, botInstance, tokens === null || tokens === void 0 ? void 0 : tokens.profileAccessToken]);
    return {
        loadBot,
        botInstance,
        launchChat,
        conversationId,
        isOpen,
        isLoading,
        isReady,
    };
};

export { useSalesforceBot };
//# sourceMappingURL=useSalesforceBot.js.map
